import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ChevronRight, Building2 } from 'lucide-react';
import { useGeoLocation } from '../../hooks/useGeoLocation';
import { useNearbyVenues, type FeedVenue } from '../../hooks/useNearbyVenues';
import './VenueStrip.css';

interface Props {
  onOpenVenue: (slug: string) => void;
}

export function VenueStrip({ onOpenVenue }: Props) {
  const { t } = useTranslation();
  const { coords, status } = useGeoLocation(true);
  const geoReady = status === 'granted' || status === 'denied' || status === 'timeout' || status === 'unavailable';

  const { items, loading } = useNearbyVenues({
    lat: coords?.lat,
    lng: coords?.lng,
    limit: 10,
    enabled: geoReady,
  });

  if (!loading && items.length === 0) return null;

  return (
    <motion.section
      className="vs-wrap"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      aria-label={t('home.venuesHeading', { defaultValue: 'Фудкорты рядом' })}
    >
      <header className="vs-head">
        <div className="vs-head-text">
          <div className="vs-eyebrow">
            <Building2 size={12} strokeWidth={2.4} />
            {t('home.venuesEyebrow', { defaultValue: 'Площадки' })}
          </div>
          <h2 className="vs-title">{t('home.venuesHeading', { defaultValue: 'Фудкорты рядом' })}</h2>
        </div>
      </header>

      <div className="vs-scroller" role="list">
        {loading && items.length === 0 && (
          <>
            {[0, 1].map((i) => (
              <div key={i} className="vs-card vs-card--skel" aria-hidden role="listitem">
                <div className="vs-shimmer" />
              </div>
            ))}
          </>
        )}

        {items.map((v, i) => (
          <VenueCard
            key={v.id}
            v={v}
            index={i}
            onOpen={() => onOpenVenue(v.slug)}
          />
        ))}
      </div>
    </motion.section>
  );
}

function VenueCard({
  v,
  index,
  onOpen,
}: {
  v: FeedVenue;
  index: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const distance =
    v.distanceKm == null
      ? null
      : v.distanceKm < 1
        ? t('feed.distanceM', { n: Math.round(v.distanceKm * 1000) })
        : t('feed.distanceKm', { n: v.distanceKm.toFixed(1) });

  // Long-press / tap → select (filter restaurants below)
  // Chevron → open venue landing
  return (
    <motion.button
      type="button"
      className="vs-card"
      role="listitem"
      onClick={onOpen}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * index, duration: 0.32, ease: 'easeOut' }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      aria-label={v.name}
    >
      <div className="vs-card-cover">
        {v.coverImageUrl ? (
          <img src={v.coverImageUrl} alt="" className="vs-card-img" />
        ) : (
          <div className="vs-card-fallback" aria-hidden>
            <span>{v.name.slice(0, 2).toUpperCase()}</span>
          </div>
        )}
        <div className="vs-card-grad" />
        <div className="vs-card-cover-info">
          <div className="vs-card-name">{v.name}</div>
          <div className="vs-card-meta">
            <span className="vs-card-count">
              {t('home.venueRestaurants', { count: v.restaurantCount, defaultValue: '{{count}} ресторанов' })}
            </span>
            {distance && <span className="vs-card-dot">·</span>}
            {distance && <span className="vs-card-distance">{distance}</span>}
          </div>
        </div>
        <div className="vs-card-chevron" aria-hidden>
          <ChevronRight size={16} strokeWidth={2.4} />
        </div>
      </div>
    </motion.button>
  );
}
